import React, { useCallback, useEffect, useState } from "react";

// IGDB's game_type ids — only the non-main ones get a label, so a DLC/bundle/port
// candidate is obvious at a glance next to the real game.
const GAME_TYPE_LABELS = {
  1: "DLC",
  2: "Expansion",
  3: "Bundle",
  4: "Standalone expansion",
  5: "Mod",
  6: "Episode",
  7: "Season",
  8: "Remake",
  9: "Remaster",
  10: "Expanded",
  11: "Port",
  12: "Fork",
  13: "Pack",
  14: "Update",
};

const ERROR_MESSAGES = {
  IGDB_UNAVAILABLE: "IGDB isn't reachable from the server right now (check IGDB_CLIENT_SECRET)",
  LAUNCHBOX_ALREADY_LINKED: "This entry is already linked to a product — unlink it there first",
  DATA_NOT_FOUND: "IGDB doesn't list that game on that platform",
};

// Manual LaunchBox → IGDB link for an unlinked entry. The search runs through
// /admin/igdb-search (server-side IGDB credentials — the panel has none of its own),
// prefilled with what the automatic flow searched for and filtered to the entry's own
// platform. A candidate is chosen by clicking one of its platform chips (a game links
// as an igdb_id+platform_id pair), then confirmed with the Link button.
function LaunchBoxIgdbLinkModal({ item, apiFetch, onLinked, onClose }) {
  const [query, setQuery] = useState(item.search_name || item.name);
  const [onlyPlatform, setOnlyPlatform] = useState(true);
  const [results, setResults] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const search = useCallback(async (text, restrictToPlatform) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSearching(true);
    setError(null);
    setSelected(null);
    try {
      const params = new URLSearchParams({ q: trimmed, launchbox_platform_id: String(item.platform_id) });
      if (!restrictToPlatform) params.set("all_platforms", "1");
      const data = await apiFetch(`/admin/igdb-search?${params.toString()}`);
      setResults(data.results);
      setPlatform(data.platform);
    } catch (err) {
      if (err.message !== "Forbidden") setError(ERROR_MESSAGES[err.message] || "IGDB search failed");
    } finally {
      setSearching(false);
    }
  }, [apiFetch, item.platform_id]);

  useEffect(() => {
    search(item.search_name || item.name, true);
  }, [search, item.search_name, item.name]);

  function handleSearch(e) {
    e.preventDefault();
    search(query, onlyPlatform);
  }

  function toggleOnlyPlatform(checked) {
    setOnlyPlatform(checked);
    search(query, checked);
  }

  async function handleLink() {
    if (!selected) return;
    setLinking(true);
    setError(null);
    try {
      await apiFetch(`/admin/launchbox-links/${item.entry_id}/link-to-igdb`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ igdb_id: selected.game.igdb_id, platform_id: selected.platform.id }),
      });
      onLinked();
      onClose();
    } catch (err) {
      if (err.message !== "Forbidden") setError(ERROR_MESSAGES[err.message] || "Couldn't link that game");
    } finally {
      setLinking(false);
    }
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal--wide admin-card glass" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 className="admin-modal-title">Link to IGDB</h3>
        <div className="admin-source-meta">
          {item.name} · {item.platform_name || `platform_id ${item.platform_id}`} · launchbox_id {item.launchbox_id}
        </div>

        <form className="admin-tgdb-link-form admin-igdb-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Name, igdb_id or igdb.com URL"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="admin-tgdb-link-input"
            autoFocus
          />
          <button type="submit" className="admin-signout-btn" disabled={searching || !query.trim()}>
            {searching ? "Searching…" : "Search"}
          </button>
        </form>
        <label className="admin-igdb-search-toggle">
          <input type="checkbox" checked={onlyPlatform} onChange={(e) => toggleOnlyPlatform(e.target.checked)} />
          {platform ? `Only ${platform.name}` : "Only this platform (no IGDB match for it — searching all)"}
        </label>

        <div className="admin-igdb-search-results">
          {results && results.length === 0 && !searching && <p className="admin-status">No IGDB results.</p>}
          {results && results.map((game) => (
            <div
              key={game.igdb_id}
              className={`admin-igdb-result ${selected?.game.igdb_id === game.igdb_id ? "admin-igdb-result--selected" : ""}`}
            >
              {game.cover_url
                ? <img className="admin-boxart admin-boxart--row" src={game.cover_url} alt={game.name} />
                : <div className="admin-boxart-placeholder admin-boxart-placeholder--row">No art</div>}
              <div className="admin-row-main">
                <div className="admin-source-name">{game.name}</div>
                <div className="admin-source-meta">
                  igdb_id {game.igdb_id}
                  {game.release_year != null && ` · ${game.release_year}`}
                  {GAME_TYPE_LABELS[game.game_type] && ` · ${GAME_TYPE_LABELS[game.game_type]}`}
                  {` · ${game.total_rating_count} ratings`}
                </div>
                <div className="admin-igdb-platforms">
                  {game.platforms.map((p) => {
                    const isSelected = selected?.game.igdb_id === game.igdb_id && selected?.platform.id === p.id;
                    const isEntryPlatform = platform?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`admin-igdb-platform-chip ${isSelected ? "admin-igdb-platform-chip--selected" : ""} ${isEntryPlatform ? "admin-igdb-platform-chip--match" : ""}`}
                        onClick={() => setSelected({ game, platform: p })}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                  {game.platforms.length === 0 && <span className="admin-row-id">No platforms on IGDB</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {error && <div className="admin-status admin-error admin-status--inline">{error}</div>}

        <div className="admin-igdb-link-footer">
          <span className="admin-row-id">
            {selected ? `${selected.game.name} · ${selected.platform.name}` : "Pick a platform on one of the results"}
          </span>
          <button type="button" className="admin-signout-btn" onClick={handleLink} disabled={!selected || linking}>
            {linking ? "Linking…" : "Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LaunchBoxIgdbLinkModal;
